import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  useMediaQuery,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

/**
 * Terms of Service page component
 * 
 * Contains the full terms of service for the MyEventShop platform
 * detailing user rights, responsibilities, and platform rules
 */
const TermsOfService: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };
  
  // Last Updated date
  const lastUpdated = "April 5, 2025";

  // Table of contents items
  const tableOfContents = [
    { title: '1. Acceptance of Terms', id: 'acceptance' },
    { title: '2. Description of Services', id: 'services' },
    { title: '3. Account Registration and Security', id: 'account' },
    { title: '4. User Content and Conduct', id: 'content' },
    { title: '5. Payment Terms', id: 'payment' },
    { title: '6. Intellectual Property Rights', id: 'ip' },
    { title: '7. Privacy and Data Protection', id: 'privacy' },
    { title: '8. Disclaimers and Limitations of Liability', id: 'liability' },
    { title: '9. Indemnification', id: 'indemnification' },
    { title: '10. Termination of Service', id: 'termination' },
    { title: '11. Modifications to Terms', id: 'modifications' },
    { title: '12. Governing Law', id: 'governing-law' },
    { title: '13. Contact Information', id: 'contact' }
  ];

  return (
    <Box>
      <AppMenu 
                onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mt: 3, mb: 2 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Typography color="text.primary">Terms of Service</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Terms of Service
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Last Updated: {lastUpdated}
          </Typography>
        </Box>

        {/* Main Content Container */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
          {/* Table of Contents */}
          <Box 
            component={Paper} 
            elevation={2}
            sx={{
              p: 3, 
              borderRadius: 2, 
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
              position: { xs: 'static', md: 'sticky' },
              top: '20px',
              alignSelf: 'flex-start',
              maxHeight: { xs: 'auto', md: 'calc(100vh - 40px)' },
              overflowY: { xs: 'visible', md: 'auto' }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>Table of Contents</Typography>
            <List sx={{ p: 0 }}>
              {tableOfContents.map((item) => (
                <ListItem 
                  key={item.id} 
                  disablePadding 
                  sx={{ mb: 1 }}
                >
                  <ListItemText 
                    primary={
                      <Link 
                        href={`#${item.id}`} 
                        underline="hover" 
                        color="text.primary"
                        sx={{ 
                          display: 'block',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Link>
                    } 
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Terms Content */}
          <Box 
            component={Paper}
            elevation={2}
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 2,
              flex: '1 1 auto',
            }}
          >
            <Box id="acceptance" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                1. Acceptance of Terms
              </Typography>
              <Typography variant="body1" paragraph>
                By accessing or using MyEventShop's website, mobile applications, or any other services offered by MyEventShop (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms constitute a legally binding agreement between you and MyEventShop regarding your use of the Services. You must be at least 18 years old to use our Services. By using the Services, you represent and warrant that you have the legal capacity to enter into this agreement.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="services" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                2. Description of Services
              </Typography>
              <Typography variant="body1" paragraph>
                MyEventShop provides a platform for users to discover, create, and promote events, including ticketing services, social media scheduling, email marketing, analytics, and community engagement features.
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify, suspend, or discontinue any aspect of our Services at any time, with or without notice. We will not be liable to you or to any third party for any such modifications, suspensions, or discontinuations.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="account" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                3. Account Registration and Security
              </Typography>
              <Typography variant="body1" paragraph>
                To access certain features of our Services, you may need to create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
              </Typography>
              <Typography variant="body1" paragraph>
                You are responsible for safeguarding your password and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account or any other breach of security.
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to disable any user account at any time if, in our opinion, you have failed to comply with these Terms or if we suspect fraudulent or abusive activity.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="content" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                4. User Content and Conduct
              </Typography>
              <Typography variant="body1" paragraph>
                Our Services may allow you to post, upload, or submit content, including text, photos, videos, and other materials ("User Content"). You retain ownership of your User Content, but you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute such User Content for the purpose of providing and promoting our Services.
              </Typography>
              <Typography variant="body1" paragraph>
                You are solely responsible for your User Content and your interactions with other users. You agree not to post User Content that:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Violates any applicable laws or regulations</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Infringes on the rights of others, including intellectual property rights</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Is harmful, abusive, defamatory, obscene, or otherwise objectionable</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Contains malware, viruses, or other harmful code</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Constitutes unauthorized commercial communications (spam)</Typography>
                </ListItem>
              </List>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                We reserve the right, but are not obligated, to monitor, edit, or remove any User Content at our sole discretion.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="payment" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                5. Payment Terms
              </Typography>
              <Typography variant="body1" paragraph>
                Certain aspects of our Services may require payment. By using our paid Services, you agree to pay all applicable fees and taxes. All payments are non-refundable unless otherwise specified.
              </Typography>
              <Typography variant="body1" paragraph>
                For event organizers, we charge a service fee based on the ticket price and the number of tickets sold. For attendees, a booking fee may be added to the ticket price. All fees will be clearly disclosed before checkout.
              </Typography>
              <Typography variant="body1" paragraph>
                We use third-party payment processors to handle all financial transactions. By using our Services, you agree to the terms and privacy policies of these payment processors.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="ip" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                6. Intellectual Property Rights
              </Typography>
              <Typography variant="body1" paragraph>
                All content, features, and functionality of our Services, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are owned by MyEventShop, its licensors, or other providers and are protected by copyright, trademark, and other intellectual property laws.
              </Typography>
              <Typography variant="body1" paragraph>
                You may not copy, modify, create derivative works from, distribute, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services without our prior written consent, except for:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Temporary storage of copies solely as necessary to view the Services</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Storage of copies in your computer's cache memory</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Printing pages from the Services for your personal, non-commercial use</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="privacy" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                7. Privacy and Data Protection
              </Typography>
              <Typography variant="body1" paragraph>
                Our Privacy Policy, available at <Link href="/privacy">https://myeventshop.com/privacy</Link>, describes how we collect, use, and share your personal information. By using our Services, you agree to our Privacy Policy.
              </Typography>
              <Typography variant="body1" paragraph>
                We comply with applicable data protection laws, including the General Data Protection Regulation (GDPR) for users in the European Union. If you have any questions about our data practices, please contact us at privacy@myeventshop.com.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="liability" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                8. Disclaimers and Limitations of Liability
              </Typography>
              <Typography variant="body1" paragraph>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMISSIBLE UNDER APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </Typography>
              <Typography variant="body1" paragraph>
                WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
              </Typography>
              <Typography variant="body1" paragraph>
                TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL MYEVENTSHOP, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICES, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="indemnification" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                9. Indemnification
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to defend, indemnify, and hold harmless MyEventShop, its affiliates, and their respective officers, directors, employees, contractors, agents, licensors, and suppliers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Your violation of these Terms</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Your User Content</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Your use of the Services</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="termination" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                10. Termination of Service
              </Typography>
              <Typography variant="body1" paragraph>
                We may terminate or suspend your account and access to the Services at any time, with or without notice, for any reason, including but not limited to your breach of these Terms.
              </Typography>
              <Typography variant="body1" paragraph>
                You may terminate your account at any time by contacting us at support@myeventshop.com. Upon termination, your right to use the Services will immediately cease.
              </Typography>
              <Typography variant="body1" paragraph>
                All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="modifications" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                11. Modifications to Terms
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify these Terms at any time. We will provide notice of any material changes by posting the updated Terms on our website and updating the "Last Updated" date at the top of these Terms.
              </Typography>
              <Typography variant="body1" paragraph>
                Your continued use of the Services after any such changes constitutes your acceptance of the new Terms. If you do not agree to the new Terms, you must stop using the Services.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="governing-law" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                12. Governing Law
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms are governed by and construed in accordance with the laws of Ireland, without giving effect to any principles of conflicts of law.
              </Typography>
              <Typography variant="body1" paragraph>
                Any legal action or proceeding arising out of or related to these Terms or the Services shall be instituted exclusively in the courts of Ireland, although we retain the right to bring any suit, action, or proceeding against you for breach of these Terms in your country of residence or any other relevant country.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="contact" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                13. Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions about these Terms, please contact us at:
              </Typography>
              <Typography variant="body1" paragraph>
                MyEventShop<br />
                123 Barrow Street<br />
                Dublin, DO4 V4X7<br />
                Ireland<br /><br />
                Email: legal@myeventshop.com<br />
                Phone: +353 (55) 123-4567
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Call to Action */}
        <Paper 
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            mb: 8,
            bgcolor: 'rgba(95, 60, 111, 0.05)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" component="p" mb={3}>
            Have questions about our Terms of Service? We're here to help.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 'medium',
            }}
          >
            Contact Us
          </Button>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default TermsOfService; 